import { DataSource, CollectionViewer } from "@angular/cdk/collections";
import { HttpParams } from '@angular/common/http';
import { Observable, Subscription, BehaviorSubject, of, EMPTY } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, tap } from 'rxjs/operators';

import { VedraxApiService } from '../../services/vedrax-api.service';
import { DescriptorPage } from '../../descriptor/descriptor-page';
import { NVP } from '../../shared/nvp';
import { FormGroup } from "@angular/forms";

/**
 * Class that implements the {@link DataSource}.
 */
export class VedraxAutocompleteDataSource extends DataSource<any[]>{

    /**
     * The subscription
     */
    private subscription: Subscription = new Subscription();

    /**
     * The data listener
     */
    private dataSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /**
     * Cache data
     */
    private cachedData: any[] = [];

    /**
     * Default page size
     */
    private pageSize = 30;

    /**
     * Current page number
     */
    private lastPage = 0;

    /**
     * The query parameters
     */
    private queryParams: HttpParams = new HttpParams();

    /**
     * The submitted flag listener
     */
    private submittedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * An observable submitted flag
     */
    public submitted$ = this.submittedSubject.asObservable();

    /**
     * The endpoint to be called
     */
    private endpoint: string = '';

    /**
     * The default params
     */
    private params: NVP[] = [];

    constructor(
        private apiService: VedraxApiService) {
        super();
    }

    setEndpoint(endpoint: string = '') {
        this.endpoint = endpoint;
    }

    setDefaultParams(nvps: NVP[] = []) {
        this.params = nvps;
    }

    /**
     * Method used for connection
     * @param collectionViewer 
     * @returns 
     */
    connect(collectionViewer: CollectionViewer): Observable<any[]> {

        //perform search on pagination
        this.subscription.add(collectionViewer.viewChange
            .pipe(
                switchMap((range) => {
                    const currentPage = this.getPageForIndex(range.end);
                    if (currentPage > this.lastPage) {
                        this.lastPage = currentPage;
                        return this.loadData();
                    }
                    return EMPTY;
                }))
            .subscribe(page => {
                if (page) {
                    const data = this.cachedData || [];
                    this.cachedData = data.concat(page.content || []);
                    this.dataSubject.next(this.cachedData);
                }
            }));

        return this.dataSubject.asObservable();
    }

    /**
     * Method used for disconnection
     * @param collectionViewer 
     */
    disconnect(collectionViewer: CollectionViewer): void {
        this.dataSubject.complete();
        this.submittedSubject.complete();
        this.subscription.unsubscribe();
    }

    /**
     * Helper method for getting the page
     * @param i 
     * @returns 
     */
    private getPageForIndex(i: number): number {
        if (i == 0) {
            return 0;
        }
        return Math.floor(i / this.pageSize);
    }

    /**
     * Method used for performing a search
     */
    initSearch(searchForm: FormGroup = new FormGroup({})) {
        this.subscription.add(searchForm.valueChanges
            .pipe(
                debounceTime(500),
                tap(() => {
                    //init search data to empty
                    this.cachedData = [];
                    //reset page
                    this.lastPage = 0;
                    //pass new query
                    this.queryParams = this.apiService.toHttpParams(searchForm.value);
                }),
                switchMap(() => this.loadData()))
            .subscribe(page => {
                this.cachedData = page.content || [];
                this.dataSubject.next(this.cachedData);
            }));
    }

    /**
    * Method for loading data from API
    */
    private loadData(): Observable<DescriptorPage> {

        this.submittedSubject.next(true);

        //1. get cache query
        let queryParams = this.queryParams;

        //2. get default parameters and add them to queryParams
        const defaultParams = this.params || [];
        defaultParams.forEach(param => {
            queryParams = queryParams.set(String(param.key), param.value);
        });

        //3. add page parameter
        if (this.lastPage) {
            queryParams = queryParams.set('page', this.lastPage);
        }

        //4. call API
        return this.apiService.get<DescriptorPage>(this.endpoint, queryParams)
            .pipe(
                catchError(() => of(new DescriptorPage())),
                finalize(() => this.submittedSubject.next(false))
            );

    }

}